import React, { useState } from 'react';
import {
  Box,
  Typography,
  Chip,
  IconButton,
  Card,
  CardContent,
  Grid,
  Divider,
  Alert,
} from '@mui/material';
import {
  Inventory as ContainerIcon,
  Close as CloseIcon,
  LocationOn as LocationIcon,
  Category as CategoryIcon,
  ContentCopy as CopyIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import { ReceptionContainer } from '../../../../core/interfaces/receptionCotnainer.interface';

interface ReceptionContainerInfoProps {
  container: ReceptionContainer;
  onClear: () => void;
}

export const ReceptionContainerInfo: React.FC<ReceptionContainerInfoProps> = ({
  container,
  onClear,
}) => {
  const [copySuccess, setCopySuccess] = useState<string | null>(null);

  const handleCopyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopySuccess(`Código "${text}" copiado al portapapeles`);
      setTimeout(() => setCopySuccess(null), 3000);
    } catch (err) {
      // Fallback
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopySuccess(`Código "${text}" copiado al portapapeles`);
      setTimeout(() => setCopySuccess(null), 3000);
    }
  };
  // Helper para traducir estados de contenedores de origen
  const translateOriginContainerStatus = (statusName: string): string => {
    const statusTranslations: { [key: string]: string } = {
      'Unprocessed': 'Sin procesar',
      'In process': 'En proceso',
      'Paused': 'Pausado',
      'Emptied': 'Vacío',
    };
    return statusTranslations[statusName] || statusName;
  };

  // Obtener el color del estado para contenedores de origen
  const getStatusColor = () => {
    const statusName = container.origin_container_status?.name;
    
    switch (statusName) {
      case 'Unprocessed':
        return 'secondary';
      case 'In process':
        return 'info';
      case 'Paused':
        return 'warning';
      case 'Emptied':
        return 'success';
      default:
        return 'default';
    }
  };

  // Obtener el texto del estado
  const getStatusText = () => {
    const statusName = container.origin_container_status?.name;
    return statusName ? translateOriginContainerStatus(statusName) : 'Sin procesar';
  };

  return (
    <Card variant="outlined" sx={{ backgroundColor: 'background.paper' }}>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Box display="flex" alignItems="center" gap={1}>
            <ContainerIcon color="primary" />
            <Typography variant="h6" fontWeight="medium">
              {container.code}
            </Typography>
            <IconButton
              size="small"
              onClick={() => handleCopyToClipboard(container.code)}
              sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main', backgroundColor: 'action.hover' } }}
              title="Copiar código al portapapeles"
            >
              <CopyIcon fontSize="small" />
            </IconButton>
          </Box>
          <IconButton
            onClick={onClear}
            size="small"
            color="error"
            aria-label="Limpiar selección"
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Divider sx={{ mb: 2 }} />

        <Grid container spacing={2}>
          {/* Estado del contenedor */}
          <Grid item xs={12}>
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <Chip
                label={getStatusText()}
                color={getStatusColor()}
                size="small"
                icon={<ContainerIcon />}
              />
            </Box>
          </Grid>

          {/* Tipo de contenedor */}
          <Grid item xs={12} sm={6}>
            <Box display="flex" alignItems="center" gap={1}>
              <CategoryIcon fontSize="small" color="action" />
              <Typography variant="caption" color="text.secondary">
                Tipo:
              </Typography>
              <Typography variant="body2" fontWeight="medium">
                Recepción
              </Typography>
            </Box>
          </Grid>

          {(container.supplier_name || container.supplier_business_name) && (
            <Grid item xs={12} sm={6}>
              <Box display="flex" alignItems="center" gap={1}>
                <BusinessIcon fontSize="small" color="action" />
                <Typography variant="caption" color="text.secondary">
                  Proveedor:
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {container.supplier_name || container.supplier_business_name}
                </Typography>
              </Box>
            </Grid>
          )}

          {(container.supplier_area_name || container.supplier_area_acronym) && (
            <Grid item xs={12} sm={6}>
              <Box display="flex" alignItems="center" gap={1}>
                <LocationIcon fontSize="small" color="action" />
                <Typography variant="caption" color="text.secondary">
                  Área:
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {container.supplier_area_acronym ? `${container.supplier_area_acronym}${container.supplier_area_name ? ' - ' : ''}` : ''}
                  {container.supplier_area_name || ''}
                </Typography>
              </Box>
            </Grid>
          )}

          {/* Referencia */}
          {container.reference && (
            <Grid item xs={12} sm={6}>
              <Box display="flex" alignItems="center" gap={1}>
                <LocationIcon fontSize="small" color="action" />
                <Typography variant="caption" color="text.secondary">
                  Referencia:
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {container.reference}
                </Typography>
              </Box>
            </Grid>
          )}
        </Grid>

        {/* Alerta de copia exitosa */}
        {copySuccess && (
          <Alert severity="success" onClose={() => setCopySuccess(null)} sx={{ mt: 2 }}>
            {copySuccess}
          </Alert>
        )}
      </CardContent>
    </Card>
  );
};
